/**
 * Scheduled Reports — admin surface for recurring report delivery.
 *
 * The backend has run real scheduled delivery for a while (scheduled_reports +
 * a Celery beat sweep + services/scheduled_report_executor, which renders xlsx/pdf
 * and emails it), but the only thing in the frontend that touched it was a demo
 * component. So a schedule could only be created by writing a row into the
 * database by hand, and nobody in the organization that receives the report could
 * see it, pause it, change who gets it, or tell whether it had ever arrived.
 *
 * This page is that surface. Deliberately small: list, create, edit/pause, run
 * now, and delivery history — the five things you need to trust a recurring email.
 *
 * Report types come from GET /api/reports/scheduled/types, which reads the
 * executor's own registry. Hardcoding the list here is how you end up with a row
 * whose report_type has no executor — the configuration that had one production
 * report failing on every beat tick, 66,275 times.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Download, Loader2, Pause, Play, Plus, RefreshCw, Send, Trash2, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { apiClient } from '@/services/apiClient';
import { PageHeader, PageContainer } from '@/components/weavein';
import { useFormattedDate } from '@/hooks/useUserTimezone';
import { PLAN_SPECIFIC_TYPES, offeredReportTypes, programsForReportType } from './planSpecificReports';

interface ScheduledReport {
  id: number;
  name: string;
  description: string | null;
  report_type: string;
  filters: Record<string, unknown> | null;
  format: string;
  frequency: string;
  day_of_week: number | null;
  day_of_month: number | null;
  hour_of_day: number | null;
  timezone: string | null;
  delivery_method: string;
  delivery_config: Record<string, unknown> | null;
  // role_terminology ids that may see and manage this schedule. null/empty = every
  // role in the org that can manage scheduled reports at all.
  visible_to_role_ids: number[] | null;
  last_run_at: string | null;
  last_run_status: string | null;
  next_run_at: string | null;
  is_active: boolean;
}

interface OrgRole {
  id: number;
  label: string;
  role_type: string;
}

interface ReportRun {
  id: number;
  started_at: string | null;
  completed_at: string | null;
  status: string | null;
  row_count: number | null;
  delivery_status: string | null;
  error_text: string | null;
}

interface ProgramOption {
  id: number;
  name: string;
  /** payers.payer_id of the program's health plan (TRUS-725), e.g. 'SFHP'. */
  payer_key?: string | null;
}

const BASE = '/api/reports/scheduled';

// Kept in step with REQUIRED_FILTERS in services/scheduled_report_executor.py. The server
// is the authority and rejects a schedule missing these; this list only decides whether to
// SHOW the picker, so a stale entry here costs a confusing error, never a broken schedule.
const REQUIRES_PROGRAM = new Set(['sfhp_invoice']);


// compute_next_run_at uses Python's weekday(), where Monday is 0 — NOT the
// "0=Sunday" the scheduled_reports DDL comment claims. Label them the way the
// scheduler actually reads them, or the report goes out on the wrong day.
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const recipientsOf = (r: ScheduledReport): string[] => {
  const cfg = r.delivery_config || {};
  const raw = (cfg as any).to ?? (cfg as any).recipients ?? [];
  return Array.isArray(raw) ? raw : typeof raw === 'string' ? [raw] : [];
};

const describeSchedule = (r: ScheduledReport): string => {
  const at = `${String(r.hour_of_day ?? 8).padStart(2, '0')}:00 UTC`;
  if (r.frequency === 'daily') return `Daily at ${at}`;
  if (r.frequency === 'weekly') return `${DAYS[r.day_of_week ?? 0]}s at ${at}`;
  if (r.frequency?.startsWith('monthly')) return `Monthly (day ${r.day_of_month ?? 1}) at ${at}`;
  return `${r.frequency} at ${at}`;
};

export default function ScheduledReportsPage() {
  const [reports, setReports] = useState<ScheduledReport[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [roles, setRoles] = useState<OrgRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [historyFor, setHistoryFor] = useState<number | null>(null);
  const [runs, setRuns] = useState<ReportRun[]>([]);
  const [showCreate, setShowCreate] = useState(false);

  // Auto-populated dates (last run, next run, run history) are stored as UTC
  // instants but must render in the timezone the logged-in user selected — the
  // same rule the rest of the app follows via useFormattedDate (TRUS-658). The
  // short zone name is resolved per-instant so a run during the other DST period
  // is still labelled correctly.
  const { fmt, timezone } = useFormattedDate();
  const fmtWhen = (v: string | null): string => {
    if (!v) return '—';
    const d = new Date(v);
    if (isNaN(d.getTime())) return v;
    let abbr = '';
    try {
      abbr =
        new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' })
          .formatToParts(d)
          .find((p) => p.type === 'timeZoneName')?.value ?? '';
    } catch {
      abbr = '';
    }
    return `${fmt.dateTime(v)}${abbr ? ` ${abbr}` : ''}`;
  };

  // Create form
  const [fName, setFName] = useState('');
  const [fType, setFType] = useState('');
  const [fFrequency, setFFrequency] = useState('weekly');
  const [fDay, setFDay] = useState(0);
  const [fHour, setFHour] = useState(8);
  const [fRecipients, setFRecipients] = useState('');
  const [fRoleIds, setFRoleIds] = useState<number[]>([]);
  // Report types whose executor cannot run without a programme — the rate card that
  // prices an SFHP invoice is per programme, so a schedule without one fails every week.
  // The server refuses these too; this is so nobody has to discover that from an error.
  const [fProgramId, setFProgramId] = useState<string>('');
  const [programs, setPrograms] = useState<ProgramOption[]>([]);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, typeList, roleList, programList] = await Promise.all([
        apiClient.get<ScheduledReport[]>(BASE),
        apiClient.get<string[]>(`${BASE}/types`).catch(() => ({ data: [] as string[] })),
        apiClient.get<OrgRole[]>(`${BASE}/roles`).catch(() => ({ data: [] as OrgRole[] })),
        apiClient.get<any>('/api/programs').catch(() => ({ data: [] })),
      ]);
      setReports(list.data || []);
      setTypes(typeList.data || []);
      setRoles(roleList.data || []);
      const progs = (Array.isArray(programList.data) ? programList.data : programList.data?.data || [])
        .map((pr: any) => ({ id: pr.id, name: pr.name, payer_key: pr.payer_key ?? null }))
        .filter((pr: ProgramOption) => pr.id != null);
      setPrograms(progs);
      const offered = offeredReportTypes(typeList.data || [], progs);
      if (!fType && offered.length) setFType(offered[0]);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setError(
          'You do not have permission to manage scheduled reports. Ask an administrator to grant your role the "View billing reports" permission.',
        );
      } else {
        setError(err?.response?.data?.detail || err?.message || 'Failed to load scheduled reports.');
      }
      setReports([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id: number, fn: () => Promise<any>, ok: string) => {
    setBusyId(id);
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(ok);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Action failed.');
    } finally {
      setBusyId(null);
    }
  };

  const toggleActive = (r: ScheduledReport) =>
    act(
      r.id,
      () => apiClient.put(`${BASE}/${r.id}`, { is_active: !r.is_active }),
      r.is_active ? `Paused "${r.name}".` : `Resumed "${r.name}".`,
    );

  const runNow = (r: ScheduledReport) => {
    const to = recipientsOf(r);
    // Send now EMAILS the recipients. Before this screen existed that was the only
    // way to see what a schedule produced, and on the first day it was live someone
    // checking the report mailed IFR's biller a third copy of it. Downloading is now
    // the way to look; sending is for actually sending, so it says who it will reach.
    if (
      !window.confirm(
        to.length
          ? `Send "${r.name}" now to ${to.join(', ')}?\n\nThis emails the report. To just look at it, use Download.`
          : `Run "${r.name}" now? It has no recipients configured, so nothing will be delivered.`,
      )
    ) {
      return;
    }
    return act(r.id, () => apiClient.post(`${BASE}/${r.id}/run-now`), `Sent "${r.name}" now.`);
  };

  // Run the report and hand back the file, without emailing anyone and without
  // moving the schedule — a download on Sunday must not skip Monday's send.
  const download = async (r: ScheduledReport) => {
    setBusyId(r.id);
    setError(null);
    setNotice(null);
    try {
      const resp = await apiClient.get(`${BASE}/${r.id}/download`, {
        params: { format: 'csv' },
        responseType: 'blob',
      });
      // The server names the file (report + date); fall back only if it did not.
      const disposition = String(resp.headers?.['content-disposition'] || '');
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename =
        match?.[1] || `${r.name.replace(/[^A-Za-z0-9]+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;

      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setNotice(`Downloaded "${r.name}".`);
    } catch (err: any) {
      // A blob responseType makes an error body arrive as a Blob, so the usual
      // err.response.data.detail is an unreadable object rather than the message.
      let detail = err?.message;
      const blob = err?.response?.data;
      if (blob instanceof Blob) {
        try {
          detail = JSON.parse(await blob.text())?.detail || detail;
        } catch {
          /* keep err.message */
        }
      } else if (blob?.detail) {
        detail = blob.detail;
      }
      setError(detail || 'Failed to download the report.');
    } finally {
      setBusyId(null);
    }
  };

  const remove = (r: ScheduledReport) => {
    if (!window.confirm(`Delete "${r.name}"? Its delivery history goes with it.`)) return;
    return act(r.id, () => apiClient.delete(`${BASE}/${r.id}`), `Deleted "${r.name}".`);
  };

  const showHistory = async (r: ScheduledReport) => {
    if (historyFor === r.id) {
      setHistoryFor(null);
      return;
    }
    setBusyId(r.id);
    try {
      const resp = await apiClient.get<ReportRun[]>(`${BASE}/${r.id}/runs`, { params: { limit: 20 } });
      setRuns(resp.data || []);
      setHistoryFor(r.id);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load history.');
    } finally {
      setBusyId(null);
    }
  };

  const create = async () => {
    const to = fRecipients
      .split(/[,\s]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    if (!fName.trim() || !fType || !to.length) {
      setError('Name, report type and at least one recipient are required.');
      return;
    }
    if (
      PLAN_SPECIFIC_TYPES[fType] &&
      fProgramId &&
      !programsForReportType(fType, programs).some((pr) => String(pr.id) === String(fProgramId))
    ) {
      setError(`This report is the ${PLAN_SPECIFIC_TYPES[fType]} file. Choose a program whose health plan is ${PLAN_SPECIFIC_TYPES[fType]}.`);
      return;
    }
    if (REQUIRES_PROGRAM.has(fType) && !fProgramId) {
      setError('Choose a program — this report is priced from that program’s rate card.');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      await apiClient.post(BASE, {
        // NO cbo_id. This used to send a literal 0 on the strength of a comment saying
        // the field was ignored. It is not ignored for a PLATFORM admin: the server took
        // the 0 at face value and resolved the schedule's org to 0 — which failed role
        // validation ("Role ids [...] do not belong to this organization") and, with the
        // role boxes left unchecked, would have created a schedule owned by no org at
        // all. The server now derives the org from the session; omitting it is correct.
        name: fName.trim(),
        report_type: fType,
        frequency: fFrequency,
        day_of_week: fFrequency === 'weekly' ? fDay : null,
        hour_of_day: fHour,
        format: 'xlsx',
        delivery_method: 'email',
        // 'to' is the key _deliver_email actually reads. The DDL comment says
        // 'emails'; a row using that key sends the file to nobody.
        delivery_config: { to },
        // Empty = unrestricted: anyone in the org who can manage scheduled reports.
        visible_to_role_ids: fRoleIds.length ? fRoleIds : null,
        // Carry the programme for the types whose executor needs one. ecm_progress_notes
        // keeps its 7-day window; everything else sends only what it actually has, rather
        // than an empty object that makes the run fail later.
        filters: {
          ...(fType === 'ecm_progress_notes' ? { days_back: 7 } : {}),
          ...(REQUIRES_PROGRAM.has(fType) && fProgramId ? { program_id: Number(fProgramId) } : {}),
        },
      });
      setNotice(`Scheduled "${fName.trim()}".`);
      setShowCreate(false);
      setFName('');
      setFProgramId('');
      setFRecipients('');
      setFRoleIds([]);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to create the schedule.');
    } finally {
      setCreating(false);
    }
  };

  // Role scope, rendered. An unscoped schedule says so explicitly rather than
  // showing an empty cell, which reads as missing data instead of "no restriction".
  const roleLabels = (r: ScheduledReport): string => {
    const ids = r.visible_to_role_ids;
    if (!ids || ids.length === 0) return 'Anyone who can manage schedules';
    const byId = new Map(roles.map((role) => [role.id, role.label]));
    return ids.map((id) => byId.get(id) || `Role ${id}`).join(', ');
  };

  const anyFailing = useMemo(() => reports.some((r) => r.last_run_status === 'failed'), [reports]);

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Reports · Delivery"
        title="Scheduled Reports"
        description="Recurring reports your organization sends by email, and whether they are actually arriving."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading} title="Refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button size="sm" onClick={() => setShowCreate((v) => !v)}>
              <Plus className="mr-1.5 h-4 w-4" />
              New schedule
            </Button>
          </div>
        }
      />
      <PageContainer>
        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {notice && (
          <div className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            {notice}
          </div>
        )}
        {anyFailing && (
          <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              A schedule below last failed. Open its history for the error — a report that fails every
              run is usually misconfigured rather than unlucky.
            </span>
          </div>
        )}

        {showCreate && (
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="text-base">New schedule</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-1">
                  <Label htmlFor="sr-name">Name</Label>
                  <Input
                    id="sr-name"
                    value={fName}
                    onChange={(e) => setFName(e.target.value)}
                    placeholder="Weekly ECM progress notes"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="sr-type">Report</Label>
                  <select
                    id="sr-type"
                    className="h-9 w-full rounded-md border border-v2border bg-surface px-2 text-sm"
                    value={fType}
                    onChange={(e) => setFType(e.target.value)}
                  >
                    {offeredReportTypes(types, programs).map((t) => (
                      <option key={t} value={t}>
                        {t.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>
                {REQUIRES_PROGRAM.has(fType) && (
                  <div className="space-y-1">
                    <Label htmlFor="sr-program">Program</Label>
                    <select
                      id="sr-program"
                      className="h-9 w-full rounded-md border border-v2border bg-surface px-2 text-sm"
                      value={fProgramId}
                      onChange={(e) => setFProgramId(e.target.value)}
                    >
                      <option value="">Select a program…</option>
                      {programsForReportType(fType, programs).map((pr) => (
                        <option key={pr.id} value={pr.id}>
                          {pr.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="space-y-1">
                  <Label htmlFor="sr-freq">Frequency</Label>
                  <select
                    id="sr-freq"
                    className="h-9 w-full rounded-md border border-v2border bg-surface px-2 text-sm"
                    value={fFrequency}
                    onChange={(e) => setFFrequency(e.target.value)}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
                {fFrequency === 'weekly' && (
                  <div className="space-y-1">
                    <Label htmlFor="sr-day">Day</Label>
                    <select
                      id="sr-day"
                      className="h-9 w-full rounded-md border border-v2border bg-surface px-2 text-sm"
                      value={fDay}
                      onChange={(e) => setFDay(Number(e.target.value))}
                    >
                      {DAYS.map((d, i) => (
                        <option key={d} value={i}>
                          {d}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="space-y-1">
                  <Label htmlFor="sr-hour">Hour (UTC)</Label>
                  <Input
                    id="sr-hour"
                    type="number"
                    min={0}
                    max={23}
                    value={fHour}
                    onChange={(e) => setFHour(Number(e.target.value))}
                  />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label>Visible to</Label>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1.5">
                    {roles.length === 0 ? (
                      <span className="text-xs text-muted-foreground">Everyone who can manage schedules</span>
                    ) : (
                      roles.map((r) => (
                        <label key={r.id} className="flex items-center gap-1.5 text-sm">
                          <input
                            type="checkbox"
                            checked={fRoleIds.includes(r.id)}
                            onChange={(e) =>
                              setFRoleIds((prev) =>
                                e.target.checked ? [...prev, r.id] : prev.filter((id) => id !== r.id),
                              )
                            }
                          />
                          {r.label}
                        </label>
                      ))
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Leave all unchecked to let anyone who can manage scheduled reports see this one.
                  </p>
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label htmlFor="sr-to">Recipients</Label>
                  <Input
                    id="sr-to"
                    value={fRecipients}
                    onChange={(e) => setFRecipients(e.target.value)}
                    placeholder="name@org.org, other@org.org"
                  />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <Button size="sm" onClick={create} disabled={creating}>
                  {creating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                  Create
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Schedules{reports.length ? ` (${reports.length})` : ''}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
              </div>
            ) : reports.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                No scheduled reports yet. Create one to have it emailed on a timer.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Report</TableHead>
                      <TableHead>Schedule</TableHead>
                      <TableHead>Recipients</TableHead>
                      <TableHead>Visible to</TableHead>
                      <TableHead>Next run</TableHead>
                      <TableHead>Last run</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reports.map((r) => (
                      <React.Fragment key={r.id}>
                        <TableRow>
                          <TableCell className="font-medium">{r.name}</TableCell>
                          <TableCell className="text-xs">{r.report_type.replace(/_/g, ' ')}</TableCell>
                          <TableCell className="whitespace-nowrap text-xs">{describeSchedule(r)}</TableCell>
                          <TableCell className="max-w-[16rem] truncate text-xs" title={recipientsOf(r).join(', ')}>
                            {recipientsOf(r).join(', ') || '—'}
                          </TableCell>
                          <TableCell className="max-w-[14rem] truncate text-xs" title={roleLabels(r)}>
                            {roleLabels(r)}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-xs">
                            {r.is_active ? fmtWhen(r.next_run_at) : '—'}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-xs">{fmtWhen(r.last_run_at)}</TableCell>
                          <TableCell>
                            {!r.is_active && r.last_run_status?.startsWith('disabled_') ? (
                              // Deactivated BY the platform, not paused by a person —
                              // say which, or a broken schedule reads as a deliberate one.
                              <Badge variant="destructive" title={r.last_run_status}>
                                {r.last_run_status === 'disabled_unknown_report_type'
                                  ? 'disabled · unknown report'
                                  : 'disabled · repeated failures'}
                              </Badge>
                            ) : !r.is_active ? (
                              <Badge variant="outline">paused</Badge>
                            ) : r.last_run_status === 'failed' ? (
                              <Badge variant="destructive">failed</Badge>
                            ) : r.last_run_status ? (
                              <Badge variant="secondary">{r.last_run_status}</Badge>
                            ) : (
                              <Badge variant="outline">never run</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Delivery history"
                                disabled={busyId === r.id}
                                onClick={() => showHistory(r)}
                              >
                                <History className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Download as CSV (does not email anyone)"
                                disabled={busyId === r.id}
                                onClick={() => download(r)}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Send now — emails the recipients"
                                disabled={busyId === r.id}
                                onClick={() => runNow(r)}
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                title={r.is_active ? 'Pause' : 'Resume'}
                                disabled={busyId === r.id}
                                onClick={() => toggleActive(r)}
                              >
                                {r.is_active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Delete"
                                disabled={busyId === r.id}
                                onClick={() => remove(r)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {historyFor === r.id && (
                          <TableRow>
                            <TableCell colSpan={9} className="bg-paper-2 p-0">
                              <div className="px-4 py-3">
                                <div className="mb-2 text-xs font-medium text-muted-foreground">
                                  Last {runs.length} run{runs.length === 1 ? '' : 's'}
                                </div>
                                {runs.length === 0 ? (
                                  <div className="text-xs text-muted-foreground">
                                    Never run. It will fire at {fmtWhen(r.next_run_at)}.
                                  </div>
                                ) : (
                                  <div className="space-y-1">
                                    {runs.map((run) => (
                                      <div key={run.id} className="flex flex-wrap items-baseline gap-x-3 text-xs">
                                        <span className="tabular-nums text-muted-foreground">
                                          {fmtWhen(run.started_at)}
                                        </span>
                                        <span
                                          className={
                                            run.status === 'succeeded' ? 'text-emerald-700' : 'text-red-700'
                                          }
                                        >
                                          {run.status}
                                        </span>
                                        {run.delivery_status && <span>delivery: {run.delivery_status}</span>}
                                        {run.row_count != null && <span>{run.row_count} rows</span>}
                                        {run.error_text && (
                                          <span className="text-red-700" title={run.error_text}>
                                            {run.error_text.slice(0, 120)}
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </PageContainer>
    </div>
  );
}
